import { supabase, getActiveOrgOverride } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { crearSaldoFavor, crearCargoManual, updatePromocion, deletePromocion } from './pagos'
import { recalcularDescuentosDeJugadores } from './promocionRecalculo'

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100

// Asignar y quitar promociones. Misma forma que planAsignacion.js.
//
// Asignar SI mueve dinero: el motor recalcula los cargos que aun no han recibido
// pagos y acredita los que si. Antes no movia nada -- la asignacion quedaba
// guardada y el descuento no llegaba a ningun recibo.
//
// Con `dryRun` se puede preguntar antes de escribir, y es lo que hace la
// pantalla: enseña cuantos cargos bajan y cuanto saldo se genera, y solo escribe
// si el admin confirma.

export const asignarPromocion = async ({ promocionId, jugadorIds = [], dryRun = false }) => {
  try {
    const ids = [...new Set(jugadorIds)]
    if (!ids.length) {
      throw new Error('Selecciona al menos un jugador, grupo o familia para asignar la promocion.')
    }
    const { data: staff } = (await getCurrentStaff()) || {}

    const { data: promo, error: promoErr } = await supabase
      .from('pagos_promociones')
      .select('*')
      .eq('id', promocionId)
      .single()
    if (promoErr) throw promoErr

    // Quienes YA la tenian: la beca otorga saldo una sola vez, y reasignar no
    // debe regalar otro. El upsert de abajo tampoco los duplica, pero necesitamos
    // saber quienes son ANTES para no crearles saldo.
    const { data: previas, error: prevErr } = await supabase
      .from('pagos_promociones_jugadores')
      .select('jugador_id')
      .eq('promocion_id', promocionId)
      .in('jugador_id', ids)
    if (prevErr) throw prevErr
    const yaTenian = new Set((previas || []).map((r) => r.jugador_id))
    const nuevos = ids.filter((id) => !yaTenian.has(id))
    const montoBecaPrevio = Number(promo?.monto_fijo || 0)

    // Preguntar sin escribir: el motor calcula como si la promocion ya estuviera
    // asignada (`extraPromociones`) y devuelve a que cargos les pega.
    if (dryRun) {
      const { data: resumen, error: rErr } = await recalcularDescuentosDeJugadores(ids, {
        temporadaId: promo?.temporada_id || null,
        dryRun: true,
        extraPromociones: [promo],
      })
      if (rErr) throw rErr
      return {
        data: {
          asignados: ids.length,
          becas: montoBecaPrevio > 0 ? nuevos.length : 0,
          montoBeca: montoBecaPrevio,
          resumen,
        },
        error: null,
      }
    }

    const rows = ids.map((jugadorId) => ({
      promocion_id: promocionId,
      jugador_id: jugadorId,
      asignado_por: staff?.id,
    }))
    // UNIQUE(promocion_id, jugador_id): reasignar a quien ya la tiene no duplica
    // ni truena, solo no hace nada.
    const { error } = await supabase
      .from('pagos_promociones_jugadores')
      .upsert(rows, { onConflict: 'promocion_id,jugador_id', ignoreDuplicates: true })
    if (error) throw error

    // BECA (monto fijo): no rebaja el cargo, otorga saldo a favor. El admin decide
    // a que cargos y por cuanto se aplica, igual que cualquier otro saldo -- y por
    // eso funciona igual sobre cargos ya emitidos que sobre los futuros.
    const montoBeca = Number(promo?.monto_fijo || 0)
    let becas = 0
    if (montoBeca > 0) {
      for (const jugadorId of nuevos) {
        const { error: sErr } = await crearSaldoFavor({
          jugadorId,
          monto: montoBeca,
          origen: 'beca',
          descripcion: `Beca "${promo.nombre}"`,
          temporadaId: promo.temporada_id || null,
          promocionId,
        })
        if (sErr) throw sErr
        becas++
      }
    }

    // PORCENTAJE: el motor rebaja los cargos que no han recibido dinero y acredita
    // los que si. Corre siempre -- para una beca no encuentra nada que rebajar y
    // termina en cero, sin necesidad de preguntarle de que tipo es.
    const { data: resumen, error: rErr } = await recalcularDescuentosDeJugadores(ids, {
      temporadaId: promo?.temporada_id || null,
    })
    if (rErr) throw rErr

    return { data: { asignados: rows.length, becas, montoBeca, resumen }, error: null }
  } catch (error) {
    console.error('[asignarPromocion]', error)
    return { data: null, error }
  }
}

// Que otorgo esta beca y en que quedo, para que la pantalla pueda preguntar con
// numeros en la mano en vez de un "¿seguro?" a ciegas.
export const getResumenBeca = async (asignacionId) => {
  try {
    const { data: asig, error: aErr } = await supabase
      .from('pagos_promociones_jugadores')
      .select('id, jugador_id, promocion_id, pagos_promociones(id, nombre, monto_fijo, temporada_id)')
      .eq('id', asignacionId)
      .single()
    if (aErr) throw aErr

    const promo = asig?.pagos_promociones
    if (!promo || !(Number(promo.monto_fijo) > 0)) {
      return { data: { esBeca: false }, error: null }
    }

    const { data: saldos, error: sErr } = await supabase
      .from('pagos_saldo_favor')
      .select('id, monto, monto_usado, saldo_disponible')
      .eq('promocion_id', asig.promocion_id)
      .eq('jugador_id', asig.jugador_id)
      .eq('activo', true)
    if (sErr) throw sErr

    const suma = (campo) => round2((saldos || []).reduce((t, x) => t + Number(x[campo] || 0), 0))
    return {
      data: {
        esBeca: true,
        nombre: promo.nombre,
        temporadaId: promo.temporada_id || null,
        jugadorId: asig.jugador_id,
        otorgado: suma('monto'),
        aplicado: suma('monto_usado'),
        disponible: suma('saldo_disponible'),
        saldoIds: (saldos || []).map((x) => x.id),
      },
      error: null,
    }
  } catch (error) {
    console.error('[getResumenBeca]', error)
    return { data: null, error }
  }
}

// El saldo que otorgo una promocion se va con ella: se retira lo que AUN NO se ha
// usado. Lo ya aplicado no se revierte -- ese dinero cerro cargos, y deshacerlo
// reabriria deudas que la familia da por pagadas.
//
// Cubre los dos origenes, beca y ajuste: `promocion_id` solo lo llevan los saldos
// que ella otorgo. Sin eso, desactivar una beca dejaba su credito vivo mientras
// que quitarsela a un jugador si lo retiraba -- la misma accion, dos resultados.
const retirarSaldosDePromocion = async (promocionId, jugadorId, { dryRun = false } = {}) => {
  const { data: saldos, error } = await supabase
    .from('pagos_saldo_favor')
    .select('id, saldo_disponible')
    .eq('promocion_id', promocionId)
    .eq('jugador_id', jugadorId)
    .eq('activo', true)
  if (error) throw error

  const vivos = (saldos || []).filter((s) => Number(s.saldo_disponible || 0) > 0)
  const monto = round2(vivos.reduce((t, s) => t + Number(s.saldo_disponible || 0), 0))
  if (dryRun || !vivos.length) return { retirados: vivos.length, monto }

  const { error: uErr } = await supabase
    .from('pagos_saldo_favor')
    .update({ saldo_disponible: 0, activo: false })
    .in('id', vivos.map((s) => s.id))
  if (uErr) throw uErr
  return { retirados: vivos.length, monto }
}

// Quitar una promocion: los cargos que no han recibido dinero vuelven a su precio
// de lista y el saldo no usado que ella otorgo se retira. Lo ya aplicado no se
// revierte; si es una beca y se quiere recuperar, `cobrarAplicado` genera un cargo
// nuevo que contra-registra en vez de borrar historia.
export const quitarPromocion = async (asignacionId, { cobrarAplicado = false, fechaVencimiento = null, conceptoId = null, dryRun = false } = {}) => {
  try {
    const { data: asig, error: aErr } = await supabase
      .from('pagos_promociones_jugadores')
      .select('id, jugador_id, promocion_id')
      .eq('id', asignacionId)
      .single()
    if (aErr) throw aErr

    const { data: beca } = await getResumenBeca(asignacionId)

    if (dryRun) {
      const { data: resumen, error: rErr } = await recalcularDescuentosDeJugadores([asig.jugador_id], {
        dryRun: true, excluirPromocionId: asig.promocion_id,
      })
      if (rErr) throw rErr
      const saldos = await retirarSaldosDePromocion(asig.promocion_id, asig.jugador_id, { dryRun: true })
      return { data: { beca: beca?.esBeca ? beca : null, resumen, saldos }, error: null }
    }

    if (beca?.esBeca) {
      if (beca.saldoIds.length) {
        const { error: rErr } = await supabase
          .from('pagos_saldo_favor')
          .update({ saldo_disponible: 0, activo: false })
          .in('id', beca.saldoIds)
        if (rErr) throw rErr
      }
      if (cobrarAplicado && beca.aplicado > 0) {
        const { error: cErr } = await crearCargoManual({
          jugadorId: beca.jugadorId,
          conceptoId,
          monto: beca.aplicado,
          fechaVencimiento: fechaVencimiento || new Date().toISOString().slice(0, 10),
          temporadaId: beca.temporadaId,
          notas: `Cancelación de beca "${beca.nombre}"`,
        })
        if (cErr) throw cErr
      }
    }

    const { error } = await supabase
      .from('pagos_promociones_jugadores')
      .delete()
      .eq('id', asignacionId)
    if (error) throw error

    // Ya sin la promocion, el motor devuelve a precio de lista los cargos que no
    // han recibido dinero. Los que si lo recibieron NO se encarecen: eso le
    // reabriria una deuda a una familia que la dio por saldada.
    const { data: resumen, error: rErr } = await recalcularDescuentosDeJugadores([asig.jugador_id], {})
    if (rErr) throw rErr

    const saldos = await retirarSaldosDePromocion(asig.promocion_id, asig.jugador_id)

    return { data: { beca: beca?.esBeca ? beca : null, resumen, saldos }, error: null }
  } catch (error) {
    console.error('[quitarPromocion]', error)
    return { data: null, error }
  }
}

// Que promociones tiene ya cada jugador. Alimenta su fila en la pantalla de
// Asignaciones, igual que getPlanesDeJugadores con los planes.
//
// Una promocion sin temporada aplica a cualquiera; con temporada, solo a la
// suya. El filtro va en JS y no en la consulta porque son dos condiciones sobre
// la tabla anidada y PostgREST no las expresa bien en un solo `or`.
export const getPromocionesDeJugadores = async (temporadaId) => {
  // OJO: pagos_promociones_jugadores NO tiene org_id. applyOrgFilter aqui
  // reventaria con "column ... org_id does not exist" -- y solo al IMPERSONAR,
  // porque ese filtro unicamente actua cuando hay override de org. El
  // aislamiento viaja por la promocion, que si lo tiene, y el !inner convierte
  // el filtro sobre la tabla embebida en un join real.
  const orgId = getActiveOrgOverride()
  let q = supabase
    .from('pagos_promociones_jugadores')
    .select('id, jugador_id, pagos_promociones!inner(id, nombre, activo, temporada_id, org_id)')
    .eq('pagos_promociones.activo', true)
  if (orgId) q = q.eq('pagos_promociones.org_id', orgId)
  const { data, error } = await q
  if (error) throw error

  const mapa = {}
  ;(data || []).forEach((r) => {
    const promo = r.pagos_promociones
    if (!promo) return
    if (temporadaId && promo.temporada_id && promo.temporada_id !== temporadaId) return
    if (!mapa[r.jugador_id]) mapa[r.jugador_id] = []
    mapa[r.jugador_id].push({
      asignacionId: r.id,
      id: promo.id,
      nombre: promo.nombre || 'Promocion',
    })
  })
  return mapa
}

// Quien tiene esta promocion. Lo necesitan editar y desactivar para saber a
// quien hay que recalcularle.
const jugadoresDePromocion = async (promocionId) => {
  const { data, error } = await supabase
    .from('pagos_promociones_jugadores')
    .select('jugador_id')
    .eq('promocion_id', promocionId)
  if (error) throw error
  return [...new Set((data || []).map((r) => r.jugador_id))]
}

// Cambiar el monto de una promocion cambia lo que deben quienes la tienen. Estas
// dos envuelven al CRUD de pagos.js para que ese recalculo no se pueda olvidar
// -- y viven aqui, no alla, porque el motor importa a pagos.js y al reves seria
// un ciclo.
export const actualizarPromocion = async (id, updates, { dryRun = false } = {}) => {
  try {
    const jugadorIds = await jugadoresDePromocion(id)
    if (dryRun) {
      const { data: resumen, error } = await recalcularDescuentosDeJugadores(jugadorIds, { dryRun: true })
      if (error) throw error
      return { data: null, resumen, error: null }
    }
    const { data, error } = await updatePromocion(id, updates)
    if (error) throw error
    const { data: resumen, error: rErr } = await recalcularDescuentosDeJugadores(jugadorIds, {})
    if (rErr) throw rErr
    return { data, resumen, error: null }
  } catch (error) {
    console.error('[actualizarPromocion]', error)
    return { data: null, resumen: null, error }
  }
}

// Desactivar es el "quitarsela a todos" de golpe. Se desactiva PRIMERO y se
// recalcula despues: el motor solo mira promociones activas, asi que en ese orden
// los cargos vuelven solos a su precio de lista.
export const desactivarPromocion = async (id, { dryRun = false } = {}) => {
  try {
    const jugadorIds = await jugadoresDePromocion(id)
    if (dryRun) {
      const { data: resumen, error } = await recalcularDescuentosDeJugadores(jugadorIds, {
        dryRun: true, excluirPromocionId: id,
      })
      if (error) throw error
      return { resumen, error: null }
    }
    const { error } = await deletePromocion(id)
    if (error) throw error
    const { data: resumen, error: rErr } = await recalcularDescuentosDeJugadores(jugadorIds, {})
    if (rErr) throw rErr
    for (const jugadorId of jugadorIds) {
      await retirarSaldosDePromocion(id, jugadorId)
    }
    return { resumen, error: null }
  } catch (error) {
    console.error('[desactivarPromocion]', error)
    return { resumen: null, error }
  }
}
